import { Autocomplete, Loader } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useMemo, useState, FocusEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import useGetAddressAutocompleteSuggestions from 'src/Hooks/Queries/useGetAddressAutocompleteSuggestions';

import { Props } from './AddressAutocomplete.types';

const AddressAutocomplete = ({
  value: placeIdFromForm,
  onChange: onFormPlaceIdChange,
  initialAddressText,
  prefill,
  description,
  placeholder,
  ...rest
}: Props) => {
  const { t } = useTranslation();
  // Text prefilled without a place id: shown, but not saved until a suggestion is picked.
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [actualInputValue, setActualInputValue] = useState<string>(() => {
    return placeIdFromForm && placeIdFromForm !== '' && initialAddressText
      ? initialAddressText
      : '';
  });

  const [debouncedInputValue] = useDebouncedValue(actualInputValue, 150);

  const [lastSelectedAddressInfo, setLastSelectedAddressInfo] = useState<
    { text: string; placeId: string } | undefined
  >(() => {
    return placeIdFromForm && placeIdFromForm !== '' && initialAddressText
      ? { text: initialAddressText, placeId: placeIdFromForm }
      : undefined;
  });

  const [shouldShowSuggestions, setShouldShowSuggestions] = useState<boolean>(true);

  const previouslySelectedTextRef = useRef<string | null>(null);

  const { data: addressSuggestions, isFetching } = useGetAddressAutocompleteSuggestions(
    debouncedInputValue,
    {
      enabled:
        shouldShowSuggestions &&
        (debouncedInputValue?.trim().length ?? 0) >= 3 &&
        placeIdFromForm === '',
    }
  );

  useEffect(() => {
    if (placeIdFromForm && placeIdFromForm !== '') {
      if (lastSelectedAddressInfo && lastSelectedAddressInfo.placeId === placeIdFromForm) {
        if (actualInputValue !== lastSelectedAddressInfo.text) {
          setActualInputValue(lastSelectedAddressInfo.text);
        }
        setShouldShowSuggestions(false);
        previouslySelectedTextRef.current = null;
      } else if (initialAddressText) {
        setActualInputValue(initialAddressText);
        setLastSelectedAddressInfo({ text: initialAddressText, placeId: placeIdFromForm });
        setShouldShowSuggestions(false);
        previouslySelectedTextRef.current = null;
      }
    } else if (!placeIdFromForm || placeIdFromForm === '') {
      if (
        actualInputValue !== '' &&
        (!initialAddressText || actualInputValue !== initialAddressText)
      ) {
        if (lastSelectedAddressInfo && lastSelectedAddressInfo.placeId) {
          setActualInputValue('');
        }
      }
      if (lastSelectedAddressInfo) {
        setLastSelectedAddressInfo(undefined);
      }
      if (actualInputValue === '') {
        setShouldShowSuggestions(true);
      }
    }
  }, [placeIdFromForm, initialAddressText, lastSelectedAddressInfo, actualInputValue]);

  // Declared after the sync effect above so the prefilled text wins when both update the input.
  useEffect(() => {
    if (!prefill?.text) return;
    previouslySelectedTextRef.current = null;
    setActualInputValue(prefill.text);
    if (prefill.placeId) {
      // Set here rather than by the parent: in the same batch as the text, otherwise the sync
      // effects below see a place id without its text and clear both.
      setLastSelectedAddressInfo({ text: prefill.text, placeId: prefill.placeId });
      onFormPlaceIdChange?.(prefill.placeId);
      setShouldShowSuggestions(false);
      setAwaitingConfirmation(false);
    } else {
      setShouldShowSuggestions(true);
      setAwaitingConfirmation(true);
    }
  }, [prefill]);

  useEffect(() => {
    if (placeIdFromForm || actualInputValue === '') {
      setAwaitingConfirmation(false);
    }
  }, [placeIdFromForm, actualInputValue]);

  useEffect(() => {
    if (
      actualInputValue === lastSelectedAddressInfo?.text &&
      placeIdFromForm === lastSelectedAddressInfo?.placeId
    ) {
      setShouldShowSuggestions(false);
      previouslySelectedTextRef.current = null;
      return;
    }

    if (actualInputValue === '') {
      if (placeIdFromForm !== '' && onFormPlaceIdChange) {
        onFormPlaceIdChange('');
      }
      previouslySelectedTextRef.current = null;
      setShouldShowSuggestions(false);
      return;
    }

    let showSuggestions = true;

    if (
      placeIdFromForm &&
      lastSelectedAddressInfo &&
      actualInputValue !== lastSelectedAddressInfo.text
    ) {
      if (onFormPlaceIdChange) {
        onFormPlaceIdChange('');
      }

      previouslySelectedTextRef.current = lastSelectedAddressInfo.text;

      if (previouslySelectedTextRef.current?.startsWith(actualInputValue)) {
        showSuggestions = false;
      }
    } else if (!placeIdFromForm && previouslySelectedTextRef.current) {
      if (previouslySelectedTextRef.current.startsWith(actualInputValue)) {
        showSuggestions = false;
      } else {
        previouslySelectedTextRef.current = null;
        showSuggestions = true;
      }
    }

    setShouldShowSuggestions(showSuggestions);
  }, [actualInputValue, lastSelectedAddressInfo, placeIdFromForm, onFormPlaceIdChange]);

  const formattedSuggestions = useMemo(
    () =>
      addressSuggestions?.suggestions
        .map(suggestion => ({
          value: suggestion.placePrediction.text.text,
          placeId: suggestion.placePrediction.placeId,
        }))
        .filter(s => s.value) ?? [],
    [addressSuggestions?.suggestions]
  );

  const dataForMantineDropdown = useMemo(() => {
    if (!shouldShowSuggestions || !actualInputValue || placeIdFromForm !== '') {
      return [];
    }
    return Array.from(new Set(formattedSuggestions.map(s => s.value)));
  }, [shouldShowSuggestions, actualInputValue, formattedSuggestions, placeIdFromForm]);

  const handleOptionSubmit = (submittedAddressText: string) => {
    const selected = formattedSuggestions.find(item => item.value === submittedAddressText);
    if (selected) {
      setActualInputValue(selected.value);
      const newSelection = { text: selected.value, placeId: selected.placeId };
      setLastSelectedAddressInfo(newSelection);
      if (onFormPlaceIdChange) {
        onFormPlaceIdChange(selected.placeId);
      }
      setShouldShowSuggestions(false);
      previouslySelectedTextRef.current = null;
    }
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (actualInputValue === '' && !placeIdFromForm) {
      setShouldShowSuggestions(true);
    } else if (actualInputValue !== lastSelectedAddressInfo?.text) {
      if (
        previouslySelectedTextRef.current &&
        previouslySelectedTextRef.current.startsWith(actualInputValue)
      ) {
        setShouldShowSuggestions(false);
      } else {
        setShouldShowSuggestions(true);
      }
    } else {
      setShouldShowSuggestions(false);
    }

    if (rest.onFocus) {
      rest.onFocus(event);
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      if (placeIdFromForm && actualInputValue !== lastSelectedAddressInfo?.text) {
        if (onFormPlaceIdChange) {
          onFormPlaceIdChange('');
        }
      }
      setShouldShowSuggestions(false);
    }, 150);

    if (rest.onBlur) {
      rest.onBlur(event);
    }
  };

  return (
    <Autocomplete
      placeholder={placeholder}
      description={description}
      {...rest}
      value={actualInputValue}
      onChange={setActualInputValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      data={dataForMantineDropdown}
      rightSection={isFetching ? <Loader size="xs" /> : null}
      error={
        rest.error ??
        (awaitingConfirmation
          ? t(
              'addressAutocomplete.confirmPrefill',
              'Choisissez l’adresse dans la liste pour l’enregistrer'
            )
          : undefined)
      }
      onOptionSubmit={handleOptionSubmit}
      filter={({ options }) => options}
    />
  );
};

export default AddressAutocomplete;
